import React, { useEffect, useState } from 'react';
import { XIcon, PlusIcon, CheckIcon, TrashIcon, PencilIcon, XCircleIcon } from 'lucide-react';
export interface User {
  id: string;
  username: string;
  password: string;
  permissions: string[];
}
interface UserManagementProps {
  onClose: () => void;
  currentUser: User;
  allModules: {
    id: string;
    title: string;
  }[];
}
export function UserManagement({
  onClose,
  currentUser,
  allModules
}: UserManagementProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Load users from localStorage on component mount
  useEffect(() => {
    const storedUsers = localStorage.getItem('users');
    if (storedUsers) {
      try {
        setUsers(JSON.parse(storedUsers));
      } catch (e) {
        console.error('Failed to parse users from localStorage', e);
        setUsers([currentUser]); // Fallback to just the current user
      }
    } else {
      setUsers([currentUser]); // Initialize with current user if no stored users
    }
  }, [currentUser]);
  // Save users to localStorage whenever they change
  useEffect(() => {
    if (users.length > 0) {
      localStorage.setItem('users', JSON.stringify(users));
    }
  }, [users]);
  const handleAddUser = () => {
    if (!newUsername || !newPassword) {
      setError('Benutzername und Passwort sind erforderlich');
      return;
    }
    // Check if username already exists
    if (users.some(user => user.username === newUsername)) {
      setError('Dieser Benutzername existiert bereits');
      return;
    }
    const newUser: User = {
      id: Date.now().toString(),
      username: newUsername,
      password: newPassword,
      permissions: [] // New users start with no permissions
    };
    setUsers([...users, newUser]);
    setNewUsername('');
    setNewPassword('');
    setError(null);
  };
  const handleDeleteUser = (userId: string) => {
    // Prevent deleting the current user
    if (userId === currentUser.id) {
      setError('Sie können Ihren eigenen Benutzer nicht löschen');
      return;
    }
    setUsers(users.filter(user => user.id !== userId));
  };
  const handleEditUser = (user: User) => {
    setEditingUser({
      ...user
    });
  };
  const handleSaveEdit = () => {
    if (!editingUser) return;
    // Check if username already exists and it's not the current editing user
    if (users.some(user => user.username === editingUser.username && user.id !== editingUser.id)) {
      setError('Dieser Benutzername existiert bereits');
      return;
    }
    setUsers(users.map(user => user.id === editingUser.id ? editingUser : user));
    setEditingUser(null);
    setError(null);
  };
  const togglePermission = (moduleId: string) => {
    if (!editingUser) return;
    const updatedPermissions = editingUser.permissions.includes(moduleId) ? editingUser.permissions.filter(id => id !== moduleId) : [...editingUser.permissions, moduleId];
    setEditingUser({
      ...editingUser,
      permissions: updatedPermissions
    });
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2509">
      <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-2510">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2511">
          <h2 className="text-xl font-bold text-gray-800" data-id="element-2512">
            Benutzerverwaltung
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-2513">
            <XIcon size={20} data-id="element-2514" />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-grow" data-id="element-2515">
          {error && <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-md" data-id="element-2516">
              <div className="flex" data-id="element-2517">
                <div className="flex-shrink-0" data-id="element-2518">
                  <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-2519" />
                </div>
                <div className="ml-3" data-id="element-2520">
                  <p className="text-sm text-red-700" data-id="element-2521">{error}</p>
                </div>
              </div>
            </div>}
          {!editingUser ? <>
              <div className="mb-6" data-id="element-2522">
                <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2523">
                  Benutzer hinzufügen
                </h3>
                <div className="flex flex-wrap gap-3" data-id="element-2524">
                  <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="Benutzername" className="flex-grow px-3 py-2 border border-gray-300 rounded-md" data-id="element-2525" />
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Passwort" className="flex-grow px-3 py-2 border border-gray-300 rounded-md" data-id="element-2526" />
                  <button onClick={handleAddUser} className="bg-[#8DC63F] hover:bg-[#7AB929] text-white px-4 py-2 rounded-md flex items-center" data-id="element-2527">
                    <PlusIcon size={16} className="mr-1" data-id="element-2528" />
                    Hinzufügen
                  </button>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2529">
                Benutzer verwalten
              </h3>
              <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" data-id="element-2530">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-2531">
                  <thead className="bg-gray-100" data-id="element-2532">
                    <tr data-id="element-2533">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2534">
                        Benutzername
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2535">
                        Berechtigungen
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2536">
                        Aktionen
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-2537">
                    {users.map(user => <tr key={user.id} data-id="element-2538">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-2539">
                          <div className="flex items-center" data-id="element-2540">
                            <div className="text-sm font-medium text-gray-900" data-id="element-2541">
                              {user.username}
                              {user.id === currentUser.id && <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full" data-id="element-2542">
                                  Aktuell
                                </span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-2543">
                          <div className="text-sm text-gray-500" data-id="element-2544">
                            {user.permissions.length > 0 ? `${user.permissions.length} Module` : 'Keine Berechtigungen'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2545">
                          <button onClick={() => handleEditUser(user)} className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-2546">
                            <PencilIcon size={16} data-id="element-2547" />
                          </button>
                          <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-900" disabled={user.id === currentUser.id} data-id="element-2548">
                            <TrashIcon size={16} className={user.id === currentUser.id ? 'opacity-50' : ''} data-id="element-2549" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </> : <div data-id="element-2550">
              <div className="mb-6" data-id="element-2551">
                <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2552">
                  Benutzer bearbeiten
                </h3>
                <div className="flex flex-wrap gap-3 mb-4" data-id="element-2553">
                  <input type="text" value={editingUser.username} onChange={e => setEditingUser({
                ...editingUser,
                username: e.target.value
              })} placeholder="Benutzername" className="flex-grow px-3 py-2 border border-gray-300 rounded-md" data-id="element-2554" />
                  <input type="password" value={editingUser.password} onChange={e => setEditingUser({
                ...editingUser,
                password: e.target.value
              })} placeholder="Neues Passwort" className="flex-grow px-3 py-2 border border-gray-300 rounded-md" data-id="element-2555" />
                </div>
                <h4 className="font-medium text-gray-700 mb-2" data-id="element-2556">
                  Modulberechtigungen
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-80 overflow-y-auto" data-id="element-2557">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-2558">
                    {allModules.map(module => <div key={module.id} className="flex items-center p-2 rounded hover:bg-gray-100" data-id="element-2559">
                        <input type="checkbox" id={`module-${module.id}`} checked={editingUser.permissions.includes(module.id)} onChange={() => togglePermission(module.id)} className="h-4 w-4 text-[#8DC63F] border-gray-300 rounded focus:ring-[#8DC63F]" data-id="element-2560" />
                        <label htmlFor={`module-${module.id}`} className="ml-2 block text-sm text-gray-900" data-id="element-2561">
                          {module.title}
                        </label>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>}
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end" data-id="element-2562">
          {editingUser ? <>
              <button onClick={() => setEditingUser(null)} className="mr-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2563">
                Abbrechen
              </button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-[#8DC63F] hover:bg-[#7AB929] text-white rounded-md flex items-center" data-id="element-2564">
                <CheckIcon size={16} className="mr-1" data-id="element-2565" />
                Speichern
              </button>
            </> : <button onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md" data-id="element-2566">
              Schließen
            </button>}
        </div>
      </div>
    </div>;
}